import { useState, useMemo, useCallback } from 'react';
import PageTitleWithTabs from 'common/components/general/PageTitleWithTabs';
import OrganizationsSelector from 'components/selectors/OrganizationsSelector';
import aiSparkIcon from '@/common/assets/svg/common/ai-spark.svg';
import libraryVars from '@/library/assets/scss/abstracts/_exports.module.scss';
import Table, { useTable, FetchingFnRequestParamsType } from '@webthatmatters/orca-table';
import NavigationGroupTabs from '@/common/components/buttons/NavigationGroupTabs';
import { getAiAnalytics } from '@/api/ai/documents/api';
import { getAiLogsAnalyticsKey } from '@/api/ai/documents/query-keys';
import { AiAnalyticsField, AiAnalyticsResponse, AiLogsTab } from '@/types/ai-logs';
import EmptyPage from '@/common/components/general/EmptyPage';
import { css } from '@emotion/react';
import useTableNavigation from '@/common/utils/hooks/useTableNavigation';
import {
  getFieldLevelMetricsGroup,
  getBenchmarksGroup,
  getColumns,
  type AiFieldOption
} from './tableConfig';
import { numberToStr } from '@/library/utils/numbers';
import useRouter from 'use-react-router';
import { parseQueryParams, stringifyObj } from '@/utils/urls';
import { AI_LOGS_TABS, DEFAULT_AI_LOGS_TAB, getAiLogsTabConfig, isAiLogsTab } from './constants';

const mapFieldsToOptions = (fields: AiAnalyticsField[]): AiFieldOption[] =>
  fields.map(field => ({
    value: field.field_name,
    label: field.display_name || field.field_name
  }));

type TopFiltersProps = {
  totals: AiAnalyticsResponse | null;
  activeTab: AiLogsTab;
  onTabChange: (tab: AiLogsTab) => void;
};

const TopFilters = ({ totals, activeTab, onTabChange }: TopFiltersProps) => {
  const tabs = useMemo(
    () =>
      AI_LOGS_TABS.map(tab => ({
        label: tab.label,
        isActive: activeTab === tab.id,
        onClick: () => onTabChange(tab.id)
      })),
    [activeTab, onTabChange]
  );

  const activeTabConfig = getAiLogsTabConfig(activeTab);

  return (
    <div className="d-flex align-items-center flex-wrap gap-3 cmb-4">
      <NavigationGroupTabs tabs={tabs} />
      {totals ? (
        <div className="fs-12 cpy-4 px-2 text-turquoise border border-turquoise rounded-xxl">
          {activeTabConfig.totalsLabel}:{' '}
          <strong>{numberToStr(totals.total_unique_completions)}</strong> | Total Field Extraction
          Attempts: <strong>{numberToStr(totals.total_field_evaluations)}</strong>
        </div>
      ) : null}
    </div>
  );
};

const AiLogs = () => {
  const { navigate } = useTableNavigation();
  const { history, location } = useRouter();

  const [selectedOrganization, setSelectedOrganization] = useState<{
    id?: string;
    uuid: string;
    name: string;
  } | null>(() => {
    const params = parseQueryParams(location.search) as {
      uuid?: string;
      name?: string;
    };
    if (params.uuid && params.name) {
      return {
        uuid: params.uuid,
        name: params.name
      };
    }
    return null;
  });

  const [activeTab, setActiveTab] = useState<AiLogsTab>(() => {
    const params = parseQueryParams(location.search) as { tab?: string };
    return isAiLogsTab(params.tab) ? params.tab : DEFAULT_AI_LOGS_TAB;
  });

  const [apiTotals, setApiTotals] = useState<AiAnalyticsResponse | null>(null);
  const [fieldOptions, setFieldOptions] = useState<AiFieldOption[]>([]);
  const [isAnalyticsFetching, setIsAnalyticsFetching] = useState(false);

  const activeTabConfig = useMemo(() => getAiLogsTabConfig(activeTab), [activeTab]);

  const columns = useMemo(
    () =>
      getColumns(
        getFieldLevelMetricsGroup(apiTotals, activeTabConfig),
        getBenchmarksGroup(apiTotals, activeTabConfig),
        { entityLabel: activeTabConfig.entityLabel, fieldOptions }
      ),
    [activeTabConfig, apiTotals, fieldOptions]
  );

  const updateSearchParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      const params = parseQueryParams(location.search);
      const newParams = { ...params, ...updates };
      const newSearch = stringifyObj(newParams);
      history.replace({
        pathname: location.pathname,
        search: newSearch
      });
    },
    [history, location.pathname, location.search]
  );

  const handleOrganizationChange = ({
    value
  }: {
    value: {
      id: string;
      uuid: string;
      name: string;
    };
  }) => {
    setSelectedOrganization(value);
    setApiTotals(null);
    setFieldOptions([]);

    updateSearchParams({
      uuid: value.uuid,
      name: value.name
    });
  };

  const handleTabChange = useCallback(
    (tab: AiLogsTab) => {
      setActiveTab(tab);
      setApiTotals(null);
      setFieldOptions([]);
      updateSearchParams({ tab });
    },
    [updateSearchParams]
  );

  const {
    refetchData,
    isFetching: isTableLoading,
    ...table
  } = useTable<AiAnalyticsField>({
    label: getAiLogsAnalyticsKey(activeTab),
    columns,
    fetchingFn: async (
      params: FetchingFnRequestParamsType,
      {
        organizationUuid,
        tabConfig
      }: {
        organizationUuid?: string;
        tabConfig?: ReturnType<typeof getAiLogsTabConfig>;
      } = {}
    ) => {
      if (!organizationUuid || !tabConfig) {
        return [];
      }

      setIsAnalyticsFetching(true);

      try {
        const filters = Array.isArray(params.filters) ? params.filters : [];
        const fieldsFilter = filters.find((f: { name: string }) => f.name === 'fields');
        const startDateFilter = filters.find((f: { name: string }) => f.name === 'start-date');
        const endDateFilter = filters.find((f: { name: string }) => f.name === 'end-date');

        const selectedFields = fieldsFilter?.value
          ? Array.isArray(fieldsFilter.value)
            ? fieldsFilter.value.map((field: string) => field)
            : undefined
          : undefined;

        const startDate = startDateFilter?.value ? String(startDateFilter.value) : undefined;
        const endDate = endDateFilter?.value ? String(endDateFilter.value) : undefined;

        const result = await getAiAnalytics({
          organizationUuid,
          documentKind: tabConfig.documentKind,
          entityType: tabConfig.entityType,
          startDate,
          endDate,
          fields: selectedFields,
          sorting: params.sorting
        });

        setApiTotals(result);

        // Keep the full field catalog when a fields filter is applied so the
        // filter dropdown is not reduced to the selected subset.
        if (!selectedFields?.length) {
          setFieldOptions(mapFieldsToOptions(result.data));
        }

        return result.data;
      } finally {
        setIsAnalyticsFetching(false);
      }
    },
    fetchingFnRequestParams: {
      organizationUuid: selectedOrganization?.uuid,
      tabConfig: activeTabConfig
    },
    navigate,
    queryOptions: {
      enabled: !!selectedOrganization?.uuid
    }
  });

  const showTableLoader = table.isPending || isTableLoading || isAnalyticsFetching;

  return (
    <div>
      <PageTitleWithTabs
        contentWrapperClassName="w-100p"
        titleComponent={
          <div className="d-flex align-items-center flex-1">
            <span className="pe-1">AI Logs</span>
            <strong className="d-flex align-items-center">/</strong>
            <div className="ms-1 min-w-248">
              <OrganizationsSelector
                getOptionValue={option => option.uuid}
                getOptionLabel={option => option.name}
                filter={{ value: selectedOrganization }}
                onChange={handleOrganizationChange}
                isMulti={false}
                styled={false}
                size="lg"
              />
            </div>
          </div>
        }
        tabs={[]}
      />

      <div className="mt-3">
        {selectedOrganization === null ? (
          <EmptyPage
            pageIcon={aiSparkIcon}
            pageText="The page has no organization selected"
            css={css`
              position: relative;
              display: flex;
              align-items: center;
              justify-content: center;
              height: 80vh;

              .empty-page__bg {
                position: absolute;
                transform: translate(-50%, 0%);
                pointer-events: none;
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 0;
                width: 600px;
                height: 530px;
                color: ${libraryVars.athensGray};
              }

              .empty-page__text {
                transform: translateY(-20%);
                position: relative;
                z-index: 1;
                font-size: 24px;
                font-weight: 400;
                text-align: center;
              }
            `}
          />
        ) : (
          <Table
            showHeaderTotals
            topFiltersComponent={
              <TopFilters totals={apiTotals} activeTab={activeTab} onTabChange={handleTabChange} />
            }
            refetchData={refetchData}
            {...table}
            isFetching={showTableLoader}
          />
        )}
      </div>
    </div>
  );
};

export default AiLogs;
